import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, CheckCircle, XCircle, Calendar, Clock, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a49fb855-3c1f-4bf2-8664-337bfce9e50d/artifacts/sokgx5c0_Gold.png";

// Logo component with proper styling
const Logo = ({ size = "h-12" }) => (
  <img 
    src={LOGO_URL} 
    alt="TC PPF" 
    className={`${size} w-auto object-contain`}
    style={{ mixBlendMode: 'lighten' }}
  />
);

const CertificateView = () => {
  const { certificateNumber } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCertificate();
  }, [certificateNumber]);

  const fetchCertificate = async () => {
    try {
      const response = await axios.get(`${API}/public/certificate/${certificateNumber}`);
      setCertificate(response.data);
    } catch (error) {
      console.error('Error fetching certificate:', error);
      setError(error.response?.data?.detail || 'Certificate not found');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      // We need to search first to get the ID
      const searchResponse = await axios.get(`${API}/public/search`, {
        params: { certificate_number: certificateNumber }
      });
      
      if (searchResponse.data.length === 0) {
        toast.error('Certificate not found');
        return;
      }
      
      const cert = searchResponse.data[0];
      
      const response = await axios.get(`${API}/certificates/${cert.id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${certificateNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificate downloaded');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download certificate. Please contact support.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-3 border-[#D4AF37] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <Card className="glass-card max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-white mb-2">Certificate Not Found</h2>
            <p className="text-zinc-400 mb-6">{error}</p>
            <Link to="/">
              <Button className="btn-gold-outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Search
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <Logo size="h-12" />
          </Link>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 py-12">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          data-testid="back-to-search"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to search
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Certificate Preview */}
          <div className="order-2 lg:order-1">
            <div className="bg-white rounded-lg shadow-2xl overflow-hidden animate-slide-up" data-testid="certificate-preview">
              {/* Professional College-Style Certificate */}
              <div className="relative px-8 py-6" style={{ background: 'linear-gradient(180deg, #FFFEF7 0%, #FFF8E7 100%)' }}>
                {/* Outer gold border */}
                <div className="absolute inset-3 border-2 border-[#D4AF37] pointer-events-none"></div>
                {/* Inner decorative border */}
                <div className="absolute inset-5 border border-[#D4AF37]/40 pointer-events-none"></div>
                
                {/* Content */}
                <div className="relative z-10 flex flex-col items-center text-center py-4">
                  {/* Logo - larger */}
                  <img src={LOGO_URL} alt="TC PPF" className="h-16 w-auto mb-2" />
                  
                  {/* Company Name */}
                  <p className="text-xs tracking-[0.3em] text-[#B8860B] uppercase font-medium mb-4">TC Protection Film</p>
                  
                  {/* Decorative line */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-16 h-px bg-[#D4AF37]"></div>
                    <div className="w-2 h-2 rotate-45 border border-[#D4AF37]"></div>
                    <div className="w-16 h-px bg-[#D4AF37]"></div>
                  </div>
                  
                  {/* Title */}
                  <h1 className="text-2xl md:text-3xl font-serif tracking-wide text-[#1a1a1a] mb-1">
                    Certificate of Achievement
                  </h1>
                  
                  {/* Subtitle */}
                  <p className="text-xs text-gray-500 tracking-wider mb-4">Professional Certification Program</p>
                  
                  {/* This certifies */}
                  <p className="text-sm text-gray-600 italic mb-2">This is to certify that</p>
                  
                  {/* Recipient Name - prominent */}
                  <div className="mb-3">
                    <h2 className="text-2xl md:text-3xl font-serif font-semibold text-[#1a1a1a] tracking-wide">
                      {certificate.first_name} {certificate.last_name}
                    </h2>
                    <div className="w-48 h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto mt-2"></div>
                  </div>
                  
                  {/* Achievement text */}
                  <p className="text-sm text-gray-600 max-w-sm leading-relaxed mb-2">
                    has successfully completed all requirements of the professional training program and is hereby recognized as a
                  </p>
                  
                  {/* Certification Title */}
                  <p className="text-lg font-bold text-[#B8860B] tracking-wide uppercase mb-4">
                    {certificate.certification_type || "Certified PPF Installation Specialist"}
                  </p>
                  
                  {/* Details Row */}
                  <div className="w-full flex justify-between items-start px-4 pt-3 border-t border-[#D4AF37]/30">
                    {/* Certificate Number */}
                    <div className="text-left">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Certificate No.</p>
                      <p className="text-sm font-semibold text-gray-700">{certificate.certificate_number}</p>
                      <p className="text-[10px] text-gray-400 mt-1">Issued: {formatDate(certificate.certification_date)}</p>
                    </div>
                    
                    {/* Signature - Center */}
                    <div className="text-center flex-1 px-4">
                      <p className="font-certificate text-2xl text-gray-700 italic" style={{ fontFamily: "'Dancing Script', cursive" }}>
                        TC Protection Film
                      </p>
                      <div className="w-32 h-px bg-[#D4AF37] mx-auto mt-1"></div>
                      <p className="text-[10px] text-gray-500 mt-1 uppercase tracking-wider">Authorized Signature</p>
                    </div>
                    
                    {/* Valid Until */}
                    <div className="text-right">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Valid Until</p>
                      <p className="text-sm font-semibold text-gray-700">{formatDate(certificate.expiration_date)}</p>
                      <p className="text-[10px] text-gray-400 mt-1">Duration: {certificate.duration_years} {certificate.duration_years === 1 ? 'Year' : 'Years'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Certificate Info */}
          <div className="order-1 lg:order-2 space-y-6 animate-fade-in">
            {/* Status Card */}
            <Card className={`glass-card overflow-hidden ${certificate.is_active ? 'border-emerald-500/30' : 'border-red-500/30'}`}>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    certificate.is_active ? 'bg-emerald-500/10' : 'bg-red-500/10'
                  }`}>
                    {certificate.is_active ? (
                      <CheckCircle className="w-8 h-8 text-emerald-500" />
                    ) : (
                      <XCircle className="w-8 h-8 text-red-500" />
                    )}
                  </div>
                  <div>
                    <h3 className={`text-2xl font-bold ${
                      certificate.is_active ? 'text-emerald-500' : 'text-red-500'
                    }`}>
                      {certificate.is_active ? 'ACTIVE' : 'EXPIRED'}
                    </h3>
                    <p className="text-zinc-400">
                      {certificate.is_active 
                        ? 'This certification is currently valid'
                        : 'This certification has expired'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Details Card */}
            <Card className="glass-card">
              <CardContent className="p-6 space-y-6">
                <div className="flex items-center gap-3">
                  <Award className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500">Certificate Holder</p>
                    <p className="text-white font-semibold text-lg">{certificate.first_name} {certificate.last_name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Award className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500">Certificate Number</p>
                    <p className="text-[#D4AF37] font-mono text-lg">{certificate.certificate_number}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-zinc-500" />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-zinc-500">Issue Date</p>
                      <p className="text-white">{formatDate(certificate.certification_date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-zinc-500" />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-zinc-500">Expiry Date</p>
                      <p className="text-white">{formatDate(certificate.expiration_date)}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-zinc-500" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500">Duration</p>
                    <p className="text-white">{certificate.duration_years} {certificate.duration_years === 1 ? 'Year' : 'Years'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Download Button */}
            <Button 
              onClick={downloadPDF}
              className="w-full btn-gold h-14 text-lg rounded-lg"
              data-testid="download-certificate"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Certificate PDF
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-zinc-500 text-sm">
            &copy; {new Date().getFullYear()} TC Protection Film. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CertificateView;
